import { Mountain } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatRuDate } from '@/lib/dateFmt';
import { SLOPE_CLASSES } from '@/lib/colorMaps';
import {
  useFieldTerrain,
  type CardinalDir,
  type TerrainMetrics,
} from '@/lib/fields/useFieldTerrain';

/**
 * FieldTerrainTab — таб «Рельеф» карточки поля (AK-207).
 *
 * Показывает метрики рельефа поля: 5 ключевых цифр (уклон ср./мин/макс,
 * экспозиция, высота, TWI) + распределение площади по 4 классам уклона.
 * Данные — GET /api/v1/fields/{id}/terrain (эпик AK-198).
 *
 * ⚠️ Stealth-AI (тикет AK-207): в тексте UI — только «Рельеф», «Уклон»,
 * «Высота», «Экспозиция». Никаких «AI/computed/ML/вычислено».
 *
 * Состояния: pending → «Считается…»; failed → ошибка; no_reference_contour →
 * «Нет утверждённого контура»; 404 → пусто. Без 500-каскадов.
 */

// Компас: латинские румбы бэкенда → русские подписи.
const CARDINAL_RU: Record<CardinalDir, string> = {
  N: 'Север',
  NE: 'Северо-восток',
  E: 'Восток',
  SE: 'Юго-восток',
  S: 'Юг',
  SW: 'Юго-запад',
  W: 'Запад',
  NW: 'Северо-запад',
};

function StatCard({
  label,
  value,
  unit,
  hint,
}: {
  label: string;
  value: string;
  unit?: string;
  hint?: string;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="mt-1 text-2xl font-semibold tabular-nums">
          {value}
          {unit && (
            <span className="ml-1 text-base font-normal text-muted-foreground">
              {unit}
            </span>
          )}
        </div>
        {hint && (
          <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
        )}
      </CardContent>
    </Card>
  );
}

function fmt(n: number, digits = 1): string {
  return n.toLocaleString('ru-RU', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function TerrainMetricsView({ m }: { m: TerrainMetrics }) {
  return (
    <div className="space-y-4">
      {/* 5 ключевых цифр */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard
          label="Уклон средний"
          value={fmt(m.slope_mean)}
          unit="°"
          hint={`мин ${fmt(m.slope_min)}° · макс ${fmt(m.slope_max)}°`}
        />
        <StatCard
          label="Экспозиция"
          value={CARDINAL_RU[m.aspect_cardinal]}
          hint={`${Math.round(m.aspect_degrees)}°`}
        />
        <StatCard
          label="Высота средняя"
          value={fmt(m.elevation_mean, 0)}
          unit="м"
          hint={`${fmt(m.elevation_min, 0)}–${fmt(m.elevation_max, 0)} м`}
        />
        <StatCard
          label="Перепад высот"
          value={fmt(m.elevation_max - m.elevation_min, 0)}
          unit="м"
        />
        <StatCard
          label="Увлажнённость (TWI)"
          value={fmt(m.twi_mean, 1)}
        />
      </div>

      {/* Распределение площади по классам уклона */}
      <Card>
        <CardContent className="p-4">
          <div className="mb-3 text-sm font-medium text-foreground">
            Распределение по крутизне
          </div>
          <div className="space-y-3">
            {SLOPE_CLASSES.map((c) => {
              const pct = (m[c.key] as number) ?? 0;
              return (
                <div key={c.key} className="flex items-center gap-3">
                  <div className="w-16 shrink-0 text-sm text-muted-foreground">
                    {c.label}
                  </div>
                  <div className="h-3 flex-1 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full transition-[width]"
                      style={{
                        width: `${Math.max(0, Math.min(100, pct))}%`,
                        backgroundColor: c.color,
                      }}
                    />
                  </div>
                  <div className="w-12 shrink-0 text-right text-sm tabular-nums">
                    {fmt(pct, 0)}%
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-xs text-muted-foreground">
            Обновлено {formatRuDate(m.computed_at)}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function FieldTerrainTab({ fieldId }: { fieldId: string }) {
  const { data, isLoading, isError, refetch } = useFieldTerrain(fieldId);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  // Сетевая/серверная ошибка (401/403/5xx) — не путать с 409 not_ready.
  if (isError || !data) {
    return (
      <Card>
        <CardContent className="flex flex-col items-start gap-2 p-6 text-muted-foreground">
          Не удалось загрузить данные рельефа.
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-accent"
          >
            Повторить
          </button>
        </CardContent>
      </Card>
    );
  }

  if (data.kind === 'computed') {
    return <TerrainMetricsView m={data.metrics} />;
  }

  // Все «не готово»-исходы рендерим как спокойное inline-состояние, не ошибку.
  let title = 'Данные рельефа недоступны';
  let body = data.kind === 'not_ready' ? data.message : '';
  if (data.kind === 'not_found') {
    body = 'Поле не найдено.';
  } else if (data.kind === 'not_ready') {
    if (data.status === 'pending') {
      title = 'Рельеф считается';
      body = 'Данные появятся, когда обработка завершится. Загляните позже.';
    } else if (data.status === 'no_reference_contour') {
      title = 'Нет утверждённого контура';
      body =
        'Чтобы получить данные рельефа, утвердите контур поля на карте.';
    } else {
      title = 'Не удалось получить данные рельефа';
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 p-12 text-center">
        <Mountain className="h-8 w-8 text-muted-foreground/60" aria-hidden />
        <div className="text-sm font-medium text-foreground">{title}</div>
        {body && (
          <div className="max-w-sm text-sm text-muted-foreground">{body}</div>
        )}
        {data.kind === 'not_ready' && data.status === 'pending' && (
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-accent"
          >
            Обновить
          </button>
        )}
      </CardContent>
    </Card>
  );
}

export default FieldTerrainTab;
